// src/pages/PortfolioPage.jsx
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { lookupCusip, fetchLivePrices, calculateGreeks, impliedVolatilityFromOptionPrice } from '../lib/api';
import { SleevePicker, ApiConfirmModal, RateLimitBanner, fmtMoney, fmtPct, fmtNum, colorClass, LoadingOverlay, ConfirmModal } from '../components';

export default function PortfolioPage() {
  const { canEdit, isOwner } = useAuth();
  const [sleeves, setSleeves] = useState([]);
  const [positions, setPositions] = useState([]);
  const [activeSleeve, setActiveSleeve] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddPosition, setShowAddPosition] = useState(false);
  const [showRefreshConfirm, setShowRefreshConfirm] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [sellTarget, setSellTarget] = useState(null);   // position to sell (realize P&L)
  const [sellPrice, setSellPrice] = useState('');
  const [sellNotes, setSellNotes] = useState('');
  const [sellProcessing, setSellProcessing] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null); // position to fully purge

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    const [{ data: sleeveData }, { data: posData }] = await Promise.all([
      supabase.from('sleeves').select('*').eq('is_active', true).order('name'),
      supabase.from('positions').select('*, securities(*)').eq('is_active', true).order('direction').order('market_value', { ascending: false })
    ]);
    setSleeves(sleeveData || []);
    setPositions(posData || []);
    setLoading(false);
  }

  async function handleRefreshPrices() {
    setRefreshing(true);
    setShowRefreshConfirm(false);
    try {
      const tickers = [...new Set(positions.map(p => p.securities?.ticker).filter(Boolean))];
      const prices = await fetchLivePrices(tickers);

      for (const pos of positions) {
        const ticker = pos.securities?.ticker;
        if (!ticker || !prices[ticker] || prices[ticker].error) continue;
        const price = prices[ticker].price;
        const mv = pos.direction === 'long' ? pos.quantity * price : -pos.quantity * price;
        const cost = pos.quantity * pos.entry_price;
        const pnl = pos.direction === 'long' ? (price - pos.entry_price) * pos.quantity : (pos.entry_price - price) * pos.quantity;

        await supabase.from('positions').update({
          current_price: price,
          market_value: mv,
          unrealized_pnl: pnl,
          unrealized_pnl_pct: cost > 0 ? (pnl / cost) * 100 : 0
        }).eq('id', pos.id);

        // Update securities price cache
        await supabase.from('securities').update({
          last_price: price,
          price_updated_at: new Date().toISOString()
        }).eq('id', pos.securities.id);
      }
      setSuccessMsg(`Refreshed ${tickers.length} securities`);
      setTimeout(() => setSuccessMsg(''), 3000);
      loadData();
    } catch (err) {
      console.error(err);
    }
    setRefreshing(false);
  }

  // SELL: realize P&L at given price, mark inactive, log exit trade
  async function handleSell() {
    if (!sellTarget) return;
    setSellProcessing(true);
    const exitPrice = parseFloat(sellPrice) || sellTarget.current_price || sellTarget.entry_price;
    const qty = sellTarget.quantity;
    const ep = sellTarget.entry_price;
    const realizedPnl = sellTarget.direction === 'long'
      ? (exitPrice - ep) * qty
      : (ep - exitPrice) * qty;
    const realizedPct = ep > 0 ? (realizedPnl / (qty * ep)) * 100 : 0;

    // Mark position inactive
    await supabase.from('positions').update({ is_active: false }).eq('id', sellTarget.id);

    // Log exit trade for P&L history
    await supabase.from('position_trades').insert({
      position_id: sellTarget.id,
      sleeve_id: sellTarget.sleeve_id,
      security_id: sellTarget.security_id,
      cusip: sellTarget.cusip,
      ticker: sellTarget.securities?.ticker,
      security_name: sellTarget.securities?.name,
      trade_type: 'exit',
      direction: sellTarget.direction,
      quantity: qty,
      price: exitPrice,
      trade_value: Math.abs(qty * exitPrice),
      entry_price: ep,
      realized_pnl: realizedPnl,
      realized_pnl_pct: realizedPct,
      traded_at: new Date().toISOString(),
      notes: sellNotes || null,
    });

    setSellTarget(null);
    setSellPrice('');
    setSellNotes('');
    setSellProcessing(false);
    loadData();
  }

  // REMOVE: fully purge — marks inactive AND deletes all position_trades records
  // Use when a position was entered by mistake and should leave no trace
  async function handleRemovePosition() {
    if (!removeTarget) return;
    // Delete all trade history for this position
    await supabase.from('position_trades').delete().eq('position_id', removeTarget.id);
    // Mark position inactive (hard delete avoided to preserve referential integrity)
    await supabase.from('positions').update({ is_active: false }).eq('id', removeTarget.id);
    setRemoveTarget(null);
    loadData();
  }

  // Legacy soft-delete (kept for ConfirmModal compatibility, now routes to remove)
  async function handleDeletePosition() {
    await supabase.from('positions').update({ is_active: false }).eq('id', deleteId);
    setDeleteId(null);
    loadData();
  }

  const filtered = activeSleeve ? positions.filter(p => p.sleeve_id === activeSleeve) : positions;
  const totalNav = filtered.reduce((s, p) => s + Math.abs(p.market_value || 0), 0);

  if (loading) return <LoadingOverlay message="Loading positions..." />;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Portfolio</div>
          <div className="page-subtitle">{filtered.length} positions</div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          {canEdit && (
            <>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowRefreshConfirm(true)} disabled={refreshing}>
                {refreshing ? '↺ Refreshing...' : '↺ Refresh Prices'}
              </button>
              <button className="btn btn-primary btn-sm" onClick={() => setShowAddPosition(true)}>+ Add Position</button>
            </>
          )}
        </div>
      </div>

      <div className="page-body section-gap">
        {successMsg && <div className="alert alert-success">✓ {successMsg}</div>}

        {canEdit && (
          <div>
            <RateLimitBanner provider="yahoo_finance" />
            <div style={{marginTop:6}}><RateLimitBanner provider="openfigi" /></div>
          </div>
        )}

        <SleevePicker sleeves={sleeves} activeId={activeSleeve} onChange={setActiveSleeve} />

        {/* Summary row */}
        <div className="stat-grid stat-grid-4">
          {[
            { label: 'Long MV', value: fmtMoney(filtered.filter(p=>p.direction==='long').reduce((s,p)=>s+(p.market_value||0),0)), cls: 'positive' },
            { label: 'Short MV', value: fmtMoney(Math.abs(filtered.filter(p=>p.direction==='short').reduce((s,p)=>s+(p.market_value||0),0))), cls: 'negative' },
            { label: 'Net MV', value: fmtMoney(filtered.reduce((s,p)=>s+(p.market_value||0),0)), cls: colorClass(filtered.reduce((s,p)=>s+(p.market_value||0),0)) },
            { label: 'Total P&L', value: fmtMoney(filtered.reduce((s,p)=>s+(p.unrealized_pnl||0),0)), cls: colorClass(filtered.reduce((s,p)=>s+(p.unrealized_pnl||0),0)) },
          ].map(s => (
            <div key={s.label} className="stat-card">
              <div className="stat-label">{s.label}</div>
              <div className={`stat-value ${s.cls}`}>{s.value}</div>
            </div>
          ))}
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">Positions</span>
            <span style={{fontSize:11,color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>Sorted by direction → exposure</span>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th>CUSIP</th>
                  <th>Direction</th>
                  <th>Sleeve</th>
                  <th>Qty</th>
                  <th>Entry</th>
                  <th>Current</th>
                  <th>Market Value</th>
                  <th>Unreal. P&L</th>
                  <th>P&L %</th>
                  <th>Wt %</th>
                  <th>Asset Class</th>
                  <th>Sector</th>
                  <th>δ Delta</th>
                  <th>γ Gamma</th>
                  <th>θ Theta</th>
                  <th>ν Vega</th>
                  {canEdit && <th></th>}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={17} style={{textAlign:'center',color:'var(--text-muted)',padding:'32px'}}>No positions. Add one to get started.</td></tr>
                ) : filtered.map(p => {
                  const s = p.securities || {};
                  const wt = totalNav > 0 ? (Math.abs(p.market_value || 0) / totalNav * 100) : 0;
                  const sleeve = sleeves.find(sl => sl.id === p.sleeve_id);
                  const isDerivative = s.asset_class === 'derivative';
                  return (
                    <tr key={p.id}>
                      <td className="primary" style={{fontWeight:500}}>{s.ticker || '—'}</td>
                      <td style={{fontSize:10,fontFamily:'var(--font-mono)',color:'var(--text-muted)'}}>{p.cusip}</td>
                      <td><span className={`badge badge-${p.direction}`}>{p.direction}</span></td>
                      <td style={{color:'var(--text-muted)'}}>{sleeve?.name || '—'}</td>
                      <td>{fmtNum(p.quantity, 0)}</td>
                      <td>${fmtNum(p.entry_price, 2)}</td>
                      <td className={p.current_price ? (p.direction === 'long' ? (p.current_price > p.entry_price ? 'positive' : 'negative') : (p.current_price < p.entry_price ? 'positive' : 'negative')) : ''}>{p.current_price ? `$${fmtNum(p.current_price,2)}` : '—'}</td>
                      <td className={colorClass(p.market_value)}>{fmtMoney(p.market_value)}</td>
                      <td className={colorClass(p.unrealized_pnl)}>{fmtMoney(p.unrealized_pnl)}</td>
                      <td className={colorClass(p.unrealized_pnl_pct)}>{fmtPct(p.unrealized_pnl_pct)}</td>
                      <td style={{color:'var(--text-muted)'}}>{fmtNum(wt,1)}%</td>
                      <td style={{fontSize:10,color:'var(--text-muted)',textTransform:'capitalize'}}>{s.asset_class?.replace(/_/g,' ') || '—'}</td>
                      <td style={{fontSize:10,color:'var(--text-muted)'}}>{s.sector || '—'}</td>
                      <td className={p.delta != null ? (p.delta >= 0 ? 'positive' : 'negative') : ''}>{isDerivative || p.delta != null ? fmtNum(p.delta,3) : <span style={{color:'var(--text-muted)'}}>—</span>}</td>
                      <td>{isDerivative || p.gamma != null ? fmtNum(p.gamma,4) : <span style={{color:'var(--text-muted)'}}>—</span>}</td>
                      <td className={p.theta != null ? 'negative' : ''}>{isDerivative || p.theta != null ? fmtNum(p.theta,3) : <span style={{color:'var(--text-muted)'}}>—</span>}</td>
                      <td>{isDerivative || p.vega != null ? fmtNum(p.vega,3) : <span style={{color:'var(--text-muted)'}}>—</span>}</td>
                      {canEdit && (
                        <td>
                          <div style={{display:'flex',gap:4}}>
                            <button
                              className="btn btn-sm"
                              style={{fontSize:10,padding:'2px 8px',background:'rgba(63,185,80,0.15)',color:'var(--green)',border:'1px solid rgba(63,185,80,0.3)'}}
                              onClick={() => { setSellTarget(p); setSellPrice(String(p.current_price || p.entry_price || '')); }}
                              title="Sell — realize P&L, keep in history"
                            >Sell</button>
                            {isOwner && (
                              <button
                                className="btn btn-sm"
                                style={{fontSize:10,padding:'2px 8px',background:'rgba(248,81,73,0.12)',color:'var(--red)',border:'1px solid rgba(248,81,73,0.25)'}}
                                onClick={() => setRemoveTarget(p)}
                                title="Remove — delete all records including history"
                              >✕</button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showAddPosition && (
        <AddPositionModal sleeves={sleeves} onClose={() => setShowAddPosition(false)} onSaved={() => { setShowAddPosition(false); loadData(); }} />
      )}

      <ApiConfirmModal
        isOpen={showRefreshConfirm}
        onConfirm={handleRefreshPrices}
        onCancel={() => setShowRefreshConfirm(false)}
        action={`Refresh live prices for ${[...new Set(positions.map(p=>p.securities?.ticker).filter(Boolean))].length} securities`}
        provider="yahoo_finance"
        estimatedCalls={[...new Set(positions.map(p=>p.securities?.ticker).filter(Boolean))].length}
      />

      <ConfirmModal
        isOpen={!!deleteId}
        title="Remove Position"
        message="Are you sure you want to remove this position? It will be marked inactive."
        onConfirm={handleDeletePosition}
        onCancel={() => setDeleteId(null)}
        danger
      />

      {/* SELL MODAL — realize P&L, keep history */}
      {sellTarget && (
        <div className="modal-backdrop">
          <div className="modal" style={{maxWidth:440}}>
            <div className="modal-header">
              <span className="modal-title">Sell Position — {sellTarget.securities?.ticker}</span>
              <button className="btn btn-icon btn-secondary" onClick={()=>setSellTarget(null)} style={{padding:'4px 8px',fontSize:14}}>✕</button>
            </div>
            <div className="modal-body">
              <div className="stat-grid stat-grid-2" style={{marginBottom:16}}>
                <div className="stat-card">
                  <div className="stat-label">Qty</div>
                  <div className="stat-value">{fmtNum(sellTarget.quantity,0)}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Entry Price</div>
                  <div className="stat-value">${fmtNum(sellTarget.entry_price,2)}</div>
                </div>
              </div>
              <div style={{marginBottom:12}}>
                <label style={{fontSize:12,color:'var(--text-secondary)',display:'block',marginBottom:4}}>Exit Price</label>
                <input
                  className="form-input"
                  type="number"
                  step="0.01"
                  value={sellPrice}
                  onChange={e=>setSellPrice(e.target.value)}
                  style={{width:'100%'}}
                  placeholder={String(sellTarget.current_price || sellTarget.entry_price)}
                />
              </div>
              {sellPrice && !isNaN(parseFloat(sellPrice)) && (() => {
                const ep = parseFloat(sellPrice);
                const pnl = sellTarget.direction==='long'
                  ? (ep - sellTarget.entry_price)*sellTarget.quantity
                  : (sellTarget.entry_price - ep)*sellTarget.quantity;
                return (
                  <div style={{background:'rgba(60,60,60,0.12)',borderRadius:'var(--radius-sm)',padding:'10px 14px',marginBottom:12}}>
                    <span style={{fontSize:12,color:'var(--text-secondary)'}}>Realized P&L: </span>
                    <strong className={colorClass(pnl)} style={{fontSize:13}}>{fmtMoney(pnl)}</strong>
                    <span style={{fontSize:11,color:'var(--text-lo)',marginLeft:8}}>
                      ({pnl >= 0 ? '+' : ''}{sellTarget.entry_price > 0 ? (pnl/(sellTarget.quantity*sellTarget.entry_price)*100).toFixed(2) : 0}%)
                    </span>
                  </div>
                );
              })()}
              <div style={{marginBottom:4}}>
                <label style={{fontSize:12,color:'var(--text-secondary)',display:'block',marginBottom:4}}>Notes (optional)</label>
                <input
                  className="form-input"
                  value={sellNotes}
                  onChange={e=>setSellNotes(e.target.value)}
                  style={{width:'100%'}}
                  placeholder="e.g. profit target hit, stop loss..."
                />
              </div>
              <div style={{marginTop:12,fontSize:11,color:'var(--text-lo)'}}>
                This marks the position closed and logs an exit trade. P&L will appear in Trade History.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setSellTarget(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleSell}
                disabled={sellProcessing}
              >
                {sellProcessing ? 'Processing...' : 'Confirm Sell'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REMOVE MODAL — purge all records */}
      {removeTarget && (
        <div className="modal-backdrop">
          <div className="modal" style={{maxWidth:420}}>
            <div className="modal-header">
              <span className="modal-title">Remove Position — {removeTarget.securities?.ticker}</span>
              <button className="btn btn-icon btn-secondary" onClick={()=>setRemoveTarget(null)} style={{padding:'4px 8px',fontSize:14}}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{color:'var(--text-secondary)',fontSize:13,marginBottom:12}}>
                Permanently remove <strong>{removeTarget.securities?.ticker || removeTarget.cusip}</strong> and all associated records?
              </p>
              <div style={{background:'rgba(160,24,24,0.08)',border:'1px solid rgba(160,24,24,0.3)',borderRadius:'var(--radius-sm)',padding:'10px 14px',fontSize:12,marginBottom:8}}>
                <strong style={{color:'var(--garnet-pale)'}}>⚠ This will delete:</strong>
                <ul style={{margin:'6px 0 0 16px',color:'var(--text-secondary)',lineHeight:1.8}}>
                  <li>The position itself</li>
                  <li>All entry and exit trade records</li>
                  <li>All realized P&L from this position</li>
                  <li>Any impact on win rate and return stats</li>
                </ul>
              </div>
              <div style={{fontSize:11,color:'var(--text-lo)'}}>
                Use Sell instead if you want to keep the P&L record. This action cannot be undone.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setRemoveTarget(null)}>Cancel</button>
              <button
                className="btn"
                style={{background:'var(--red)',color:'#fff',border:'none'}}
                onClick={handleRemovePosition}
              >
                Remove All Records
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// ADD POSITION MODAL
// ============================================================
function AddPositionModal({ sleeves, onClose, onSaved }) {
  const [step, setStep] = useState('enter'); // 'enter' | 'confirm' | 'saving'
  const [form, setForm] = useState({ cusip: '', quantity: '', direction: 'long', entry_price: '', asset_class: '', sector: '', notes: '', sleeve_id: sleeves[0]?.id || '' });
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [showApiConfirm, setShowApiConfirm] = useState(false);
  // Derivative fields
  const [showGreeks, setShowGreeks] = useState(false);
  const [greeksForm, setGreeksForm] = useState({ option_type: 'call', strike: '', expiry: '', iv: '0.25', risk_free: '0.0525' });
  const [calculatedGreeks, setCalculatedGreeks] = useState(null);

  const set = (k, v) => setForm(f => ({...f, [k]: v}));

  async function handleLookup() {
    if (!form.cusip.trim()) return;
    setShowApiConfirm(true);
  }

  async function doLookup() {
    setShowApiConfirm(false);
    setLookupLoading(true);
    setLookupError('');
    const result = await lookupCusip(form.cusip.trim().toUpperCase());
    if (result) {
      setLookupResult(result);
      setShowGreeks(result.securityType?.includes('Option') || false);

      // Auto-fill fields from lookup + attempt to pull live price (option symbol if OCC, otherwise ticker)
      try {
        const idType = (result.detectedIdType || '').toLowerCase();
        const raw = form.cusip.trim().toUpperCase();
        const priceKeys = [];
        if (idType === 'occ') {
          priceKeys.push(raw); // option symbol
          if (result.ticker) priceKeys.push(result.ticker); // underlying
          // Auto-derive option params from identifier when available
          if (result.expiry || result.strike || result.option_type) {
            setGreeksForm(g => ({
              ...g,
              option_type: result.option_type || g.option_type,
              strike: result.strike != null ? String(result.strike) : g.strike,
              expiry: result.expiry || g.expiry
            }));
          }
        } else if (result.ticker) {
          priceKeys.push(result.ticker);
        }

        if (priceKeys.length) {
          const pxMap = await fetchLivePrices([...new Set(priceKeys)]);
          const primaryKey = idType === 'occ' ? raw : result.ticker;
          const primaryPx = pxMap?.[primaryKey]?.price;
          if (!form.entry_price && primaryPx != null && !Number.isNaN(primaryPx)) {
            setForm(f => ({ ...f, entry_price: String(primaryPx) }));
          }

          if (idType === 'occ') {
            const optPx = pxMap?.[raw]?.price;
            const undPx = result.ticker ? pxMap?.[result.ticker]?.price : null;
            const K = result.strike != null ? Number(result.strike) : Number(greeksForm.strike);
            const expiryStr = result.expiry || greeksForm.expiry;
            if (optPx != null && undPx != null && K && expiryStr) {
              const expDate = new Date(expiryStr);
              const T = Math.max(0, (expDate.getTime() - Date.now()) / (365.25 * 24 * 3600 * 1000));
              const r = 0.05;
              const sigma = impliedVolatilityFromOptionPrice(Number(optPx), Number(undPx), K, T, r, result.option_type || greeksForm.option_type || 'call');
              if (sigma != null && Number.isFinite(sigma)) {
                setGreeksForm(g => ({ ...g, iv: String(Number(sigma.toFixed(4))) }));
                const gk = calculateGreeks(Number(undPx), K, T, r, sigma, result.option_type || greeksForm.option_type || 'call');
                setCalculatedGreeks(gk);
              }
            }
          }
        }
      } catch (e) {}

    } else {
      setLookupError('Could not find security. You can still proceed and fill details manually.');
    }
    setLookupLoading(false);
  }

  function calcGreeksFromForm() {
    if (!lookupResult || !form.entry_price || !greeksForm.strike || !greeksForm.expiry) return;
    const S = parseFloat(form.entry_price);
    const K = parseFloat(greeksForm.strike);
    const expDate = new Date(greeksForm.expiry);
    const T = Math.max(0, (expDate - Date.now()) / (1000 * 60 * 60 * 24 * 365));
    const r = parseFloat(greeksForm.risk_free);
    const sigma = parseFloat(greeksForm.iv);
    const g = calculateGreeks(S, K, T, r, sigma, greeksForm.option_type);
    setCalculatedGreeks(g);
  }

  async function handleSave() {
    // Manual requirements
    if (!form.asset_class) {
      setLookupError('Asset Type is required.');
      setStep('enter');
      return;
    }
    if (!form.sector || !form.sector.trim()) {
      setLookupError('Sector is required.');
      setStep('enter');
      return;
    }

    setStep('saving');
    try {
      // Upsert security
      const secData = {
        cusip: form.cusip.trim().toUpperCase(),
        ticker: lookupResult?.ticker || null,
        name: lookupResult?.name || null,
        asset_class: form.asset_class || null,
        exchange: lookupResult?.exchCode || null,
        // Persist GICS sector + industry from Yahoo enrichment (returned by edge function)
        sector: form.sector?.trim() || null,
        industry: lookupResult?.industry || null,
        ...(showGreeks ? {
          option_type: greeksForm.option_type,
          strike: parseFloat(greeksForm.strike) || null,
          expiry: greeksForm.expiry || null
        } : {})
      };

      const { data: secResult } = await supabase.from('securities').upsert(secData, { onConflict: 'cusip' }).select().single();
      const secId = secResult?.id;
      if (!secId) throw new Error('Failed to save security');

      // Save position
      const qty = parseFloat(form.quantity);
      const price = parseFloat(form.entry_price);
      const mv = form.direction === 'long' ? qty * price : -qty * price;

      await supabase.from('positions').insert({
        sleeve_id: form.sleeve_id,
        security_id: secId,
        cusip: form.cusip.trim().toUpperCase(),
        quantity: qty,
        direction: form.direction,
        entry_price: price,
        current_price: price,
        market_value: mv,
        unrealized_pnl: 0,
        unrealized_pnl_pct: 0,
        notes: form.notes?.trim() || null,
        ...(calculatedGreeks ? {
          delta: calculatedGreeks.delta,
          gamma: calculatedGreeks.gamma,
          theta: calculatedGreeks.theta,
          vega: calculatedGreeks.vega,
          rho: calculatedGreeks.rho,
          delta_adjusted_exposure: Math.abs(calculatedGreeks.delta) * qty * price
        } : {})
      });

      onSaved();
    } catch (err) {
      console.error(err);
      setStep('enter');
    }
  }

  return (
    <>
      <div className="modal-backdrop">
        <div className="modal">
          <div className="modal-header">
            <span className="modal-title">Add Position</span>
            <button className="btn btn-icon btn-secondary" onClick={onClose} style={{padding:'4px 8px', fontSize:14}}>✕</button>
          </div>
          <div className="modal-body">
            {step === 'saving' ? (
              <LoadingOverlay message="Saving position..." />
            ) : (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Identifier *</label>
                    <div style={{display:'flex',gap:6}}>
                      <input className="form-input" value={form.cusip} onChange={e=>set('cusip',e.target.value.toUpperCase())} placeholder="CUSIP / ISIN / FIGI / OCC Option / Ticker" maxLength={32} style={{fontFamily:'var(--font-mono)'}} />
                      <button className="btn btn-secondary btn-sm" onClick={handleLookup} disabled={!form.cusip || lookupLoading} style={{whiteSpace:'nowrap'}}>
                        {lookupLoading ? '...' : 'Lookup'}
                      </button>
                    </div>
                    {lookupError && <div style={{fontSize:11,color:'var(--yellow)',marginTop:4}}>{lookupError}</div>}
                    {lookupResult && (
                      <div style={{marginTop:6,padding:'8px 10px',background:'var(--green-dim)',border:'1px solid rgba(63,185,80,0.3)',borderRadius:'var(--radius-sm)'}}>
                        <div style={{fontSize:12,color:'var(--green)',fontFamily:'var(--font-mono)',fontWeight:500}}>{lookupResult.ticker}</div>
                        <div style={{fontSize:11,color:'var(--text-secondary)',marginTop:2}}>{lookupResult.name}</div>
                        {lookupResult.exchCode && <div style={{fontSize:10,color:'var(--text-muted)'}}>{lookupResult.exchCode} · {lookupResult.securityType}</div>}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Sleeve *</label>
                    <select className="form-select" value={form.sleeve_id} onChange={e=>set('sleeve_id',e.target.value)}>
                      {sleeves.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                </div>

                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label className="form-label">Direction *</label>
                    <select className="form-select" value={form.direction} onChange={e=>set('direction',e.target.value)}>
                      <option value="long">Long</option>
                      <option value="short">Short</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Quantity / Shares *</label>
                    <input className="form-input" type="number" value={form.quantity} onChange={e=>set('quantity',e.target.value)} placeholder="1000" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Entry Price (USD) *</label>
                    <input className="form-input" type="number" value={form.entry_price} onChange={e=>set('entry_price',e.target.value)} placeholder="150.00" step="0.01" />
                  </div>
                </div>

                {form.quantity && form.entry_price && (
                  <div style={{background:'var(--bg-elevated)',borderRadius:'var(--radius-sm)',padding:'8px 12px',marginBottom:14,fontSize:12,fontFamily:'var(--font-mono)',color:'var(--text-secondary)',display:'flex',gap:16}}>
                    <span>Entry MV: <span style={{color:'var(--text-primary)'}}>{fmtMoney(parseFloat(form.quantity)*parseFloat(form.entry_price))}</span></span>
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Asset Type *</label>
                    <select className="form-select" value={form.asset_class} onChange={e=>set('asset_class',e.target.value)}>
                      <option value="">Select...</option>
                      <option value="us_equity">US Equity</option>
                      <option value="intl_equity">Intl Equity</option>
                      <option value="etf">ETF</option>
                      <option value="fixed_income">Fixed Income</option>
                      <option value="derivative">Derivative / Option</option>
                      <option value="crypto">Crypto</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Sector *</label>
                    <input
                      className="form-input"
                      value={form.sector}
                      onChange={e=>set('sector',e.target.value)}
                      placeholder={lookupResult?.sector || "e.g. Technology"}
                    />
                    {lookupResult?.sector && !form.sector && (
                      <button
                        type="button"
                        style={{fontSize:10,color:'var(--accent)',background:'none',border:'none',cursor:'pointer',padding:'2px 0',marginTop:2}}
                        onClick={() => set('sector', lookupResult.sector)}
                      >Use: {lookupResult.sector}</button>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <input className="form-input" value={form.notes || ''} onChange={e=>set('notes',e.target.value)} placeholder="Optional trade notes..." />
                </div>

                {/* Derivative / Options Greeks */}
                <div style={{marginTop:8}}>
                  <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:12,color:'var(--text-secondary)'}}>
                    <input type="checkbox" checked={showGreeks} onChange={e=>setShowGreeks(e.target.checked)} />
                    This is an option / derivative — enter Greeks
                  </label>
                </div>

                {showGreeks && (
                  <div style={{marginTop:12,padding:'12px 14px',background:'var(--bg-elevated)',borderRadius:'var(--radius-sm)',border:'1px solid var(--border)'}}>
                    <div style={{fontSize:11,fontWeight:600,color:'var(--text-secondary)',marginBottom:10,textTransform:'uppercase',letterSpacing:'0.06em'}}>Option Parameters</div>
                    <div className="form-row form-row-3">
                      <div className="form-group">
                        <label className="form-label">Option Type</label>
                        <select className="form-select" value={greeksForm.option_type} onChange={e=>{setGreeksForm(g=>({...g,option_type:e.target.value}));setCalculatedGreeks(null);}}>
                          <option value="call">Call</option>
                          <option value="put">Put</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Strike Price</label>
                        <input className="form-input" type="number" step="0.01" value={greeksForm.strike} onChange={e=>{setGreeksForm(g=>({...g,strike:e.target.value}));setCalculatedGreeks(null);}} placeholder="150.00" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Expiry Date</label>
                        <input className="form-input" type="date" value={greeksForm.expiry} onChange={e=>{setGreeksForm(g=>({...g,expiry:e.target.value}));setCalculatedGreeks(null);}} />
                      </div>
                    </div>
                    <div className="form-row form-row-3">
                      <div className="form-group">
                        <label className="form-label">Implied Vol (σ)</label>
                        <input className="form-input" type="number" step="0.01" value={greeksForm.iv} onChange={e=>{setGreeksForm(g=>({...g,iv:e.target.value}));setCalculatedGreeks(null);}} placeholder="0.25" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Risk-Free Rate</label>
                        <input className="form-input" type="number" step="0.001" value={greeksForm.risk_free} onChange={e=>setGreeksForm(g=>({...g,risk_free:e.target.value}))} placeholder="0.0525" />
                      </div>
                      <div className="form-group" style={{display:'flex',alignItems:'flex-end'}}>
                        <button className="btn btn-secondary btn-sm" style={{width:'100%'}} onClick={calcGreeksFromForm} type="button">
                          Calculate Greeks
                        </button>
                      </div>
                    </div>
                    {calculatedGreeks && (
                      <div style={{display:'flex',gap:12,flexWrap:'wrap',marginTop:8,padding:'8px 10px',background:'var(--bg-base)',borderRadius:'var(--radius-sm)',fontSize:11,fontFamily:'var(--font-mono)'}}>
                        {[['δ Delta',calculatedGreeks.delta?.toFixed(4)],['γ Gamma',calculatedGreeks.gamma?.toFixed(5)],['θ Theta',calculatedGreeks.theta?.toFixed(4)],['ν Vega',calculatedGreeks.vega?.toFixed(4)],['ρ Rho',calculatedGreeks.rho?.toFixed(4)]].map(([l,v])=>(
                          <span key={l} style={{color:'var(--text-secondary)'}}>{l}: <strong style={{color:'var(--text-primary)'}}>{v}</strong></span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {step !== 'saving' && (
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose} type="button">Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                type="button"
                disabled={!form.cusip || !form.quantity || !form.entry_price || !form.sleeve_id}
              >
                Add Position
              </button>
            </div>
          )}
        </div>
      </div>

      <ApiConfirmModal
        isOpen={showApiConfirm}
        onConfirm={doLookup}
        onCancel={() => setShowApiConfirm(false)}
        action={`Look up identifier ${form.cusip} via OpenFIGI`}
        provider="openfigi"
        estimatedCalls={1}
      />
    </>
  );
}
